import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneIncoming, UserPlus, X } from 'lucide-react';
import SaveNumberForm from './SaveNumberForm';
import { MATCH_KIND_LABEL, callerTitle } from '../../utils/calls';
import type { Call, CallCardData } from '../../types/sales';

/**
 * One ringing call, as the incoming-call card shows it: who it is (the
 * customer, contact, supplier or staff member whose number it is), the
 * number, and what this organisation would want to know before answering:
 * open orders, open follow-ups, and the newest pinned note on the customer.
 * A number not on file can be saved to a customer from here; `onHold` keeps
 * the card up while that form is open, even once the call is answered.
 */
export default function IncomingCallCard({ call, onDismiss, onHold, onSaved }: {
  call: CallCardData;
  onDismiss: () => void;
  onHold?: (holding: boolean) => void;
  onSaved?: (call: Call) => void;
}) {
  const [saving, setSaving] = useState(false);
  const title = callerTitle(call);
  const openOrders = call.customer?.open_orders;
  const openFollowUps = call.customer?.open_follow_ups;
  const openIssues = call.customer?.open_issues;
  const pinned = call.customer?.pinned_note;
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="pointer-events-auto bg-white border border-emerald-300 shadow-lg rounded-xl p-4 min-w-0"
    >
      <div className="flex items-start gap-3 min-w-0">
        <span className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <PhoneIncoming className="h-5 w-5 text-emerald-700 animate-pulse" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Incoming call</p>
          <p className="text-base font-semibold text-neutral-900 break-words">{title}</p>
          <p className="text-sm text-neutral-600 break-words">
            {call.matched_kind ? MATCH_KIND_LABEL[call.matched_kind] : 'Not on file'}
            {call.caller_number && title !== call.caller_number ? ` · ${call.caller_number}` : ''}
            {call.extension ? ` · ext ${call.extension}` : ''}
          </p>
          {openOrders != null && (
            <p className="text-sm text-neutral-600">
              {openOrders === 0 ? 'No open orders' : openOrders === 1 ? '1 open order' : `${openOrders} open orders`}
            </p>
          )}
          {!!openFollowUps && (
            <p className="text-sm text-amber-800">
              {openFollowUps === 1 ? '1 follow-up open' : `${openFollowUps} follow-ups open`}
              {openIssues ? `, ${openIssues === 1 ? '1 issue' : `${openIssues} issues`}` : ''}
            </p>
          )}
          {pinned && (
            <p className="mt-1 text-sm text-neutral-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 line-clamp-3 whitespace-pre-wrap break-words">
              {pinned}
            </p>
          )}
          {!call.matched_kind && call.caller_number && (
            saving ? (
              <div className="mt-3">
                <SaveNumberForm callId={call.id} number={call.caller_number}
                  onSaved={(saved) => { onHold?.(false); setSaving(false); onSaved?.(saved); }}
                  onCancel={() => { onHold?.(false); setSaving(false); }} />
              </div>
            ) : (
              <button type="button" onClick={() => { onHold?.(true); setSaving(true); }}
                className="mt-2 flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline">
                <UserPlus className="h-4 w-4" aria-hidden />
                Save number to a customer
              </button>
            )
          )}
          {call.customer_id && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
              <Link to={`/customers/${call.customer_id}`} className="font-medium text-primary-700 hover:underline" onClick={onDismiss}>
                Open customer
              </Link>
              <Link to={`/customers/${call.customer_id}?tab=calls`} className="text-primary-700 hover:underline" onClick={onDismiss}>
                Past calls
              </Link>
              <Link to={`/customers/${call.customer_id}?tab=notes`} className="text-primary-700 hover:underline" onClick={onDismiss}>
                Notes
              </Link>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss incoming call"
          className="p-1 rounded-lg text-neutral-500 hover:bg-neutral-100 shrink-0"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
